import { Outlet, useNavigate } from 'react-router-dom'
import { SideNav } from '@/components/shell/SideNav'
import { BottomTabBar } from '@/components/shell/BottomTabBar'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { SottovoWordmark } from '@/components/brand'
import { useUser } from '@/stores/appStore'
import { useAuth } from '@/hooks/useAuth'
import { useMeetingActive } from '@/stores/meetingActiveStore'
import { cn } from '@/utils/cn'

export interface AppLayoutProps {
  /**
   * 在席中(アクティブな会議セッション)かどうか。テスト用の明示的な注入口。
   * 未指定時は `useMeetingActive()`（`MeetingLivePage` が同期するグローバルフラグ）
   * から自動判定する（Task 32: モバイル下端 UI 競合対策）。
   */
  meetingActive?: boolean
}

/**
 * リデザイン v2 認証後アプリの骨格レイアウト (旧 DashboardLayout の置き換え)。
 *
 * - デスクトップ (md 以上): 左に SideNav、右にヘッダ + メイン (<Outlet/>)
 * - モバイル (md 未満): 上部ミニヘッダ + メイン + 下部 BottomTabBar
 *
 * 子の認証ページ (まだ Ant のものを含む) は <Outlet/> 内にそのまま描画される。
 * ルート要素に `.app-v2` を付与し v2 トークンスコープを有効化する。
 *
 * 注: WorkspaceSwitcher は CreateWorkspaceModal 連携が必要なため本フェーズでは省略
 *     (/settings/workspaces から利用可能)。後続フェーズでヘッダに統合する。
 */
export function AppLayout({ meetingActive }: AppLayoutProps = {}) {
  const navigate = useNavigate()
  const user = useUser()
  const { logout } = useAuth()
  const globalMeetingActive = useMeetingActive()
  const isMeetingActive = meetingActive ?? globalMeetingActive

  const displayName = user?.name ?? ''

  async function handleLogout() {
    await logout()
  }

  const header = (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-v2-border bg-v2-surface px-4 md:px-6">
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        aria-label="Sottovo ホーム"
        className="flex items-center rounded-v2-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
      >
        <SottovoWordmark size={22} />
      </button>

      <div className="flex items-center gap-3">
        {displayName ? (
          <span className="hidden items-center gap-2 text-sm text-v2-ink-muted sm:flex">
            <Avatar fallback={displayName.charAt(0)} alt={displayName} size="sm" />
            {displayName}
          </span>
        ) : null}
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          ログアウト
        </Button>
      </div>
    </header>
  )

  return (
    <div className="app-v2 flex min-h-screen w-full bg-v2-bg text-v2-ink">
      {/* デスクトップ サイドナビ */}
      <div className="hidden md:block">
        <SideNav className="sticky top-0 h-screen" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {header}
        {/*
          モバイルは下部 BottomTabBar 分の余白 (pb-20) を確保する。
          在席中 (isMeetingActive) は BottomTabBar 自体を非表示にするため、
          AudioBar との下端競合を避ける目的でも余白を詰める (Task 32)。
        */}
        <main
          className={cn(
            'min-w-0 flex-1 px-4 pt-6 md:px-8 md:pb-8',
            isMeetingActive ? 'pb-4' : 'pb-20',
          )}
        >
          <Outlet />
        </main>
      </div>

      {/* モバイル ボトムナビ: 在席中は AudioBar との下端競合を避けるため非表示にする (Task 32) */}
      {!isMeetingActive ? (
        <div className="md:hidden">
          <BottomTabBar />
        </div>
      ) : null}
    </div>
  )
}
